/**
 * Dashboard summary + usage rollup hooks (P30 / B-path).
 *
 *   GET /api/v1/dashboard/summary    — Home metric tiles
 *   GET /api/v1/usage/rollups        — Usage Analytics aggregate
 *   GET /api/v1/sessions             — empty stub V1
 *   GET /api/v1/triggers             — empty stub V1
 */
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from './client.js';

export interface DashboardSummary {
  servers: { active: number; total: number };
  tools: { active: number; total: number };
  approvals: { pending: number };
  clients: { active: number; total: number };
  virtual_servers: { total: number };
  policies: { active: number; total: number };
  audit_24h: { allow: number; deny: number; error: number; unknown: number };
  audit_total_all_time: number;
  generated_at: string;
}

export interface UsageRollups {
  window_hours: number;
  total_events_all_time: number;
  by_method: { method: string; count: number }[];
  by_hour: { ts: string; count: number }[];
  decision_histogram: { allow: number; deny: number; error: number; unknown: number };
}

interface SessionsListResponse {
  items: unknown[];
  data_source?: string;
  detail?: string;
}

interface TriggersListResponse {
  items: unknown[];
  data_source?: string;
  detail?: string;
}

export function useDashboardSummary() {
  return useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => apiFetch<DashboardSummary>('/api/v1/dashboard/summary'),
  });
}

export function useUsageRollups(hours = 24) {
  return useQuery({
    queryKey: ['usage', 'rollups', hours],
    queryFn: () => apiFetch<UsageRollups>(`/api/v1/usage/rollups?hours=${hours}`),
  });
}

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => apiFetch<SessionsListResponse>('/api/v1/sessions'),
  });
}

export function useTriggers() {
  return useQuery({
    queryKey: ['triggers'],
    queryFn: () => apiFetch<TriggersListResponse>('/api/v1/triggers'),
  });
}
